import React, { useState } from 'react';
import styled from 'styled-components';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  overflow: hidden;
`;

/* -- Sidebar -- */
const PlaygroundSidebar = styled.aside`
  width: 280px;
  border-right: 1px solid var(--border);
  background: var(--sidebar-bg);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: 768px) { display: none; }
`;

const SidebarHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);

  h3 { font-size: 0.9375rem; margin-bottom: 0.75rem; }
`;

const AgentSelect = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
`;

const NewConvoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: var(--accent);
  color: white;
  transition: all 0.2s;

  &:hover { background: var(--accent-hover); }
`;

const SidebarSectionTitle = styled.div`
  padding: 1rem 1.25rem 0;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const SessionList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
`;

const SessionListItem = styled.div<{ $active?: boolean }>`
  padding: 0.625rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.25rem;
  background: ${({ $active }) => $active ? 'var(--accent-subtle)' : 'transparent'};

  &:hover { background: var(--bg-elevated); }
`;

const SessionTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SessionTime = styled.div`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

/* -- Chat Main -- */
const ChatMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ChatHeader = styled.div`
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChatAgentBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChatAgentIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8125rem;
`;

const ChatAgentName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ChatAgentEnv = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const Btn = styled.button<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: transparent;
  color: var(--text-secondary);

  &:hover { background: var(--bg-elevated); color: var(--text-primary); }
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Msg = styled.div<{ $role: 'user' | 'agent' | 'system' }>`
  display: flex;
  gap: 0.75rem;
  max-width: 720px;
  animation: fadeIn 0.3s ease;
  ${({ $role }) => $role === 'user' ? 'align-self: flex-end; flex-direction: row-reverse;' : ''}
  ${({ $role }) => $role === 'system' ? 'align-self: center; max-width: 480px;' : ''}
`;

const MsgAvatar = styled.div<{ $bg?: string; $color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg || 'var(--accent-subtle)'};
  color: ${({ $color }) => $color || 'var(--accent)'};
`;

const MsgBody = styled.div`
  max-width: 85%;
`;

const MsgBubble = styled.div<{ $role: 'user' | 'agent' | 'system' }>`
  padding: 0.875rem 1.125rem;
  border-radius: 16px;
  font-size: 0.9375rem;
  line-height: 1.6;
  ${({ $role }) =>
    $role === 'agent' ? 'background: var(--bg-surface); border: 1px solid var(--border); color: var(--text-secondary); border-top-left-radius: 4px;'
    : $role === 'user' ? 'background: var(--accent); color: white; border-top-right-radius: 4px;'
    : 'background: var(--info-subtle); border: 1px solid rgba(59, 130, 246, 0.2); color: var(--info); font-size: 0.8125rem; text-align: center; border-radius: 8px;'}
`;

const MsgMeta = styled.div<{ $right?: boolean }>`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${({ $right }) => $right ? 'text-align: right; justify-content: flex-end;' : ''}
`;

const DelegationTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  color: var(--warning);
  font-weight: 500;
  margin-bottom: 0.375rem;
`;

const ThinkingBubble = styled.div`
  padding: 0.875rem 1.125rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  border-top-left-radius: 4px;
`;

const ThinkingLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.375rem;
`;

const ThinkingText = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  font-style: italic;
`;

const ThinkingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0.5rem 0 0;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent);
    opacity: 0.4;
    animation: pulse-dot 1.4s ease-in-out infinite;
  }
  span:nth-child(2) { animation-delay: 0.2s; }
  span:nth-child(3) { animation-delay: 0.4s; }
`;

/* Input area */
const ChatInputArea = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border);
  background: var(--bg-surface);
`;

const ChatInputContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const ChatInputWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 0.5rem;
  transition: border-color 0.2s;

  &:focus-within { border-color: var(--accent); }
`;

const ChatTextarea = styled.textarea`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  outline: none;
  resize: none;
  max-height: 160px;
  line-height: 1.5;

  &::placeholder { color: var(--text-muted); }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding-bottom: 0.125rem;
`;

const InputActionBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  transition: all 0.2s;

  &:hover { background: var(--bg-elevated); color: var(--text-primary); }
`;

const SendBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--accent);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.2s;

  &:hover { background: var(--accent-hover); }
`;

const InputHints = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const InputHint = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);

  kbd {
    font-family: inherit;
    padding: 1px 4px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 3px;
    font-size: 0.625rem;
  }
`;

/* ---------- component ---------- */
const ChatPlayground: React.FC = () => {
  const [showInspector, setShowInspector] = useState(false);

  return (
    <PageBody>
      <PlaygroundSidebar>
        <SidebarHeader>
          <h3>Playground</h3>
          <AgentSelect>
            <option>Emily (Management)</option>
            <option>Emily-Menu (Conversational)</option>
            <option>Emily-Reservations (Task)</option>
            <option>Martin (General)</option>
            <option>Data Extractor (Data)</option>
          </AgentSelect>
        </SidebarHeader>

        <NewConvoBtn>&#x2795; New Conversation</NewConvoBtn>

        <SidebarSectionTitle>Recent Conversations</SidebarSectionTitle>
        <SessionList>
          <SessionListItem $active>
            <SessionTitle>Table reservation for Saturday</SessionTitle>
            <SessionTime>2 minutes ago</SessionTime>
          </SessionListItem>
          <SessionListItem>
            <SessionTitle>Gluten-free menu options</SessionTitle>
            <SessionTime>42 minutes ago</SessionTime>
          </SessionListItem>
          <SessionListItem>
            <SessionTitle>Wine pairing recommendations</SessionTitle>
            <SessionTime>2 hours ago</SessionTime>
          </SessionListItem>
          <SessionListItem>
            <SessionTitle>Large party booking inquiry</SessionTitle>
            <SessionTime>Yesterday</SessionTime>
          </SessionListItem>
          <SessionListItem>
            <SessionTitle>Menu prices for Valentine&apos;s special</SessionTitle>
            <SessionTime>Yesterday</SessionTime>
          </SessionListItem>
        </SessionList>
      </PlaygroundSidebar>

      <ChatMain>
        <ChatHeader>
          <ChatAgentBadge>
            <ChatAgentIcon>E</ChatAgentIcon>
            <div>
              <ChatAgentName>Emily</ChatAgentName>
              <ChatAgentEnv>Management &middot; Sandbox</ChatAgentEnv>
            </div>
          </ChatAgentBadge>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Btn onClick={() => setShowInspector(!showInspector)}>&#x1F50D; Inspect</Btn>
            <Btn>&#x2B07; Export</Btn>
          </div>
        </ChatHeader>

        <ChatMessages>
          <Msg $role="system">
            <MsgBody style={{ maxWidth: '100%' }}>
              <MsgBubble $role="system">
                New conversation started with Emily (Management Agent) in Sandbox environment
              </MsgBubble>
            </MsgBody>
          </Msg>

          <Msg $role="user">
            <MsgAvatar $bg="var(--bg-elevated)" $color="var(--text-secondary)">SW</MsgAvatar>
            <MsgBody>
              <MsgBubble $role="user">
                Hi Emily, I&apos;d like to reserve a table for 4 this Saturday evening, preferably something with a nice view.
              </MsgBubble>
              <MsgMeta $right>10:42 AM</MsgMeta>
            </MsgBody>
          </Msg>

          <Msg $role="agent">
            <MsgAvatar>E</MsgAvatar>
            <MsgBody>
              <MsgBubble $role="agent">
                Welcome, valued guest! I&apos;d be delighted to help you find the perfect table. Let me check availability for a party of 4 with a view.
              </MsgBubble>
              <MsgMeta>
                <span>10:42 AM</span>
                <span>&middot;</span>
                <span style={{ color: 'var(--accent)' }}>Delegating to emily-reservations</span>
              </MsgMeta>
            </MsgBody>
          </Msg>

          <Msg $role="agent">
            <MsgAvatar $bg="var(--warning-subtle)" $color="var(--warning)">ER</MsgAvatar>
            <MsgBody>
              <DelegationTag>&#x1F504; via Emily-Reservations</DelegationTag>
              <MsgBubble $role="agent" style={{ borderLeft: '3px solid var(--warning)' }}>
                Great news! I&apos;ve found several options with lovely views:
                <br /><br />
                <strong>1. Garden Terrace</strong> &mdash; 7:30 PM<br />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Open-air seating surrounded by our herb garden.</span>
                <br /><br />
                <strong>2. Rooftop Lounge</strong> &mdash; 8:00 PM<br />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Panoramic city views with ambient lighting.</span>
                <br /><br />
                <strong>3. Window Alcove</strong> &mdash; 6:30 PM<br />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Intimate corner with floor-to-ceiling windows.</span>
                <br /><br />
                Which option appeals to you?
              </MsgBubble>
              <MsgMeta>
                <span>10:42 AM</span>
                <span>&middot;</span>
                <span>1.2s</span>
                <span>&middot;</span>
                <span style={{ color: 'var(--success)' }}>Task: check_availability &#x2713;</span>
              </MsgMeta>
            </MsgBody>
          </Msg>

          <Msg $role="user">
            <MsgAvatar $bg="var(--bg-elevated)" $color="var(--text-secondary)">SW</MsgAvatar>
            <MsgBody>
              <MsgBubble $role="user">
                The rooftop lounge at 8 PM sounds amazing! Let&apos;s go with that.
              </MsgBubble>
              <MsgMeta $right>10:43 AM</MsgMeta>
            </MsgBody>
          </Msg>

          {/* Thinking indicator */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', maxWidth: 720 }}>
            <MsgAvatar $bg="var(--warning-subtle)" $color="var(--warning)">ER</MsgAvatar>
            <ThinkingBubble>
              <ThinkingLabel>Emily-Reservations is thinking...</ThinkingLabel>
              <ThinkingText>Executing make_reservation task for Rooftop Lounge at 8:00 PM, party of 4...</ThinkingText>
              <ThinkingDots>
                <span /><span /><span />
              </ThinkingDots>
            </ThinkingBubble>
          </div>
        </ChatMessages>

        <ChatInputArea>
          <ChatInputContainer>
            <ChatInputWrap>
              <ChatTextarea rows={1} placeholder="Type a message..." />
              <InputActions>
                <InputActionBtn title="Voice input">&#x1F3A4;</InputActionBtn>
                <InputActionBtn title="Attach file">&#x1F4CE;</InputActionBtn>
                <SendBtn title="Send">&#x2191;</SendBtn>
              </InputActions>
            </ChatInputWrap>
            <InputHints>
              <InputHint><kbd>Enter</kbd> to send</InputHint>
              <InputHint><kbd>Shift+Enter</kbd> for new line</InputHint>
              <InputHint>Type <kbd>/</kbd> for commands</InputHint>
            </InputHints>
          </ChatInputContainer>
        </ChatInputArea>
      </ChatMain>
    </PageBody>
  );
};

export default ChatPlayground;
